/**
 * CompleteWeeklyReviewSheet — J5-B2 Wave 6 redesign (R1.b)
 *
 * Slide-out FormSheet for completing a weekly operating review.
 * Promoted from Modal to FormSheet per design rule R1.b (any Modal containing
 * a textarea is promoted to Sheet for ergonomic free-text writing).
 *
 * Redesign intent: weekly review completion is a reflective writing task, not
 * a quick form fill. Each of the three sections (key findings, action items,
 * 3 Mu's) gets generous vertical space and contextual helper text so the
 * operator can think and write without feeling cramped. Visual dividers and
 * section headings create clear mental separation between the three distinct
 * reflection lenses.
 *
 * Fields:
 *   - summary (key findings / summary — required)
 *   - actionItems (optional)
 *   - threeMus (3 Mu's observations — optional)
 *
 * No URL routing: this is a transactional submission tied to the cadence cycle,
 * not a shareable artifact (per AC-W6-01 decision).
 *
 * Public API: (isOpen, onClose, onSubmit, isPending) — unchanged from the
 * inline CompleteReviewDialog it replaces.
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { FormSheet } from '@/components/ui/FormSheet';
import { Textarea } from '@/components/ui/Textarea';
import { Alert } from '@/components/ui/Alert';

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type WeeklyReviewSnapshot = {
  /** Key findings and overall summary for the week (required). */
  summary: string;
  /** Action items to carry forward — one per line. */
  actionItems: string;
  /** Observations on the 3 Mu's: Muda, Muri, Mura. */
  threeMus: string;
};

function diffWeeklyReviewSnapshot(
  base: WeeklyReviewSnapshot,
  cur: WeeklyReviewSnapshot,
): Partial<Record<keyof WeeklyReviewSnapshot, boolean>> {
  return {
    summary: base.summary !== cur.summary,
    actionItems: base.actionItems !== cur.actionItems,
    threeMus: base.threeMus !== cur.threeMus,
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface CompleteWeeklyReviewSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    summary: string;
    action_items: string;
    three_mus_notes: string;
  }) => void;
  isPending: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CompleteWeeklyReviewSheet({
  isOpen,
  onClose,
  onSubmit,
  isPending,
}: CompleteWeeklyReviewSheetProps) {
  // ─── Form state via useFormSnapshot ───────────────────────────────────────
  const { state, setField, baseline, current } = useFormSnapshot<WeeklyReviewSnapshot>({
    initialFactory: () => ({
      summary: '',
      actionItems: '',
      threeMus: '',
    }),
    isOpen,
  });

  const { summary, actionItems, threeMus } = state;

  // ─── Validation ───────────────────────────────────────────────────────────
  const [validationError, setValidationError] = useState('');

  // Reset validation error when sheet opens.
  useEffect(() => {
    if (isOpen) setValidationError('');
  }, [isOpen]);

  const handleSubmit = () => {
    if (!summary.trim()) {
      setValidationError(t('Findings / summary is required'));
      return;
    }
    setValidationError('');
    // DESIGN NOTE (LEAN-05 Weekly Review — F-Ref-29, decided 2026-05-25):
    // The three-mus review is intentionally captured as a single freetext field.
    // At save time, the value maps to muda.details only; muri and Mura are left empty by design.
    // Structured fields (separate Muda/Muri/Mara textareas) are deferred until UX research
    // confirms the need. Do NOT "fix" the apparently missing muri/mara fields — this is deliberate.
    // See: roros-polish-wave-1-phase-0.md § Open questions → OQ-1 for context.
    onSubmit({
      summary,
      action_items: actionItems,
      three_mus_notes: threeMus,
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <FormSheet<WeeklyReviewSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={t('Complete Weekly Review')}
      mode="create"
      baseline={baseline}
      current={current}
      diff={diffWeeklyReviewSnapshot}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      submitLabel={t('Complete Review')}
      submittingLabel={t('Completing…')}
      submitTestId="complete-review-submit"
      width="md"
    >
      <div className="space-y-0">
        {/* Validation error */}
        {validationError && (
          <Alert variant="error" className="mb-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{validationError}</span>
            </div>
          </Alert>
        )}

        {/* ── Section 1: Key findings / summary ──────────────────────────── */}
        <section className="pb-6">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('Key findings / summary')}
              {' '}
              <span className="text-error-500 font-normal" aria-hidden>*</span>
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('Summarise this week: what went well, what needs attention, and what surprised you.')}
            </p>
          </div>
          <Textarea
            value={summary}
            onChange={(e) => {
              setField('summary', e.target.value);
              if (validationError) setValidationError('');
            }}
            placeholder={t('Summarise this week: what went well, what needs attention...')}
            className="min-h-[200px] resize-y"
            required
            aria-required="true"
            data-testid="weekly-review-summary"
          />
        </section>

        <hr className="border-border" />

        {/* ── Section 2: Action items ─────────────────────────────────────── */}
        <section className="py-6">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t('Action items')}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('List the specific actions you are committing to before next week — one per line.')}
            </p>
          </div>
          <Textarea
            value={actionItems}
            onChange={(e) => setField('actionItems', e.target.value)}
            placeholder={t('Actions to take before next week...')}
            className="min-h-[200px] resize-y"
            data-testid="weekly-review-action-items"
          />
        </section>

        <hr className="border-border" />

        {/* ── Section 3: 3 Mu's notes ─────────────────────────────────────── */}
        <section className="pt-6">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-foreground">
              {t("3 Mu's notes")}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('Note observations on waste (Muda), overload (Muri), and unevenness (Mura) spotted this week.')}
            </p>
          </div>
          <Textarea
            value={threeMus}
            onChange={(e) => setField('threeMus', e.target.value)}
            placeholder={t('Any observations on waste, overload, or unevenness...')}
            className="min-h-[200px] resize-y"
            data-testid="weekly-review-three-mus"
          />
        </section>
      </div>
    </FormSheet>
  );
}
